import React from "react";
import projectService from "../features/projects/services/projectService";
import { Project } from "../types/project";

export interface ProjectsContextValue {
  projects: Project[];
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
}

const ProjectsContext = React.createContext<ProjectsContextValue | null>(null);

export const ProjectsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  const fetchProjects = React.useCallback(async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(
        Array.isArray(data) ? data : (Object.values(data || {}) as Project[])
      );
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = React.useCallback(async () => {
    setError(false);
    await fetchProjects();
  }, [fetchProjects]);

  React.useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const value = React.useMemo(
    () => ({ projects, loading, error, refresh }),
    [projects, loading, error, refresh]
  );

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  );
};

export function useProjects(): ProjectsContextValue {
  const ctx = React.useContext(ProjectsContext);
  if (!ctx) {
    throw new Error("useProjects must be used inside ProjectsProvider");
  }
  return ctx;
}
